'use client'

import { getPromo } from '@/actions/stripe'
import ButtonCustom from '@/components/button-custom'
import { Discount } from '@/components/icons/discount-icon'
import {
  promotionCodeSelector,
  setPromotionSelector,
} from '@/hooks/use-cart-product/selectors'
import { useProductStore } from '@/hooks/use-cart-product/store'
import { useOutsideClick } from '@/hooks/use-outside-click'
import {
  autoFocusDiscountSelector,
  setAutoFocusDiscountSelector,
  setDetailOpenSelector,
} from '@/hooks/use-payment-controller/selectors'
import { usePaymentStore } from '@/hooks/use-payment-controller/store'
import { useTracking } from '@/hooks/use-tracking'
import { zodResolver } from '@hookform/resolvers/zod'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { TextField } from '@repo/ui/components/forms/text-field'
import { z } from 'zod'
import { useTrackingStore } from '@/hooks/use-tracking/store'
import { qParamsSelector } from '@/hooks/use-tracking/selectors'

const schema = z.object({
  promotionCode: z.string().optional(),
})

type FormDiscountProps = {
  promotionCode: string
}

type LayoutDiscountProps = {
  openDetail: boolean
}

export function LayoutDiscount({ openDetail }: LayoutDiscountProps) {
  const [isSubmit, setIsSubmit] = useState(false)
  const [promoCode, setPromoCode] = useState(null)
  const [statusDiscountForm, setStatusUser] = React.useState<{
    status: 'initial' | 'processing' | 'error'
  }>({ status: 'initial' })
  const setDetailOpen = usePaymentStore(setDetailOpenSelector)
  const setAutofocusSelector = usePaymentStore(setAutoFocusDiscountSelector)
  const isAutoFocus = usePaymentStore(autoFocusDiscountSelector)

  const promotion = useProductStore(promotionCodeSelector)
  const setPromotion = useProductStore(setPromotionSelector)
  const { qParams, deleteKeyFromParam } = useTrackingStore(qParamsSelector)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid, isDirty },
  } = useForm<{
    promotionCode: string
  }>({
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (isAutoFocus) {
      setIsSubmit(true)
    }
    setAutofocusSelector(false)
  }, [isAutoFocus, isSubmit, setAutofocusSelector])

  useEffect(() => {
    if (qParams?.promo || promotion?.code) {
      if (qParams?.promo && qParams.promo !== promoCode) {
        getPromo(qParams?.promo)
          .then((res) => {
            if (res.success) {
              setPromotion({
                code: res.data.code,
                promotionId: res.data.promotionId,
                couponId: res.data.couponId,
                promotionAmount: res.data.promotionAmount,
                promotionPercentage: res.data.promotionPercentage,
                promoApplyToProducts: res.data.promoApplyToProducts,
              })
              setPromoCode(res.data.code)
            } else {
              deleteKeyFromParam('promo')
            }
          })
          .catch(() => {
            deleteKeyFromParam('promo')
          })
      }
      if (!qParams?.promo && promotion?.code && promoCode !== promotion?.code) {
        setPromoCode(promotion?.code)
      }
    }
    if (promoCode && !promotion?.code && !qParams?.promo) {
      setPromoCode(null)
    }
  }, [
    deleteKeyFromParam,
    promoCode,
    promotion?.code,
    qParams.promo,
    setPromotion,
  ])

  const tracking = useTracking()

  const onSubmit = async (data: FormDiscountProps) => {
    try {
      if (statusDiscountForm.status === 'processing') return

      setStatusUser({ status: 'processing' })

      // TODO: promotion code redeem

      const {
        success,
        message,
        data: promo,
      } = await getPromo(data.promotionCode)

      if (!success) {
        setError('promotionCode', { message })
        setStatusUser({ status: 'error' })
        return
      }
      setPromotion({
        code: promo.code,
        promotionId: promo.promotionId,
        couponId: promo.couponId,
        promotionAmount: promo.promotionAmount,
        promotionPercentage: promo.promotionPercentage,
        promoApplyToProducts: promo.promoApplyToProducts,
      })
      setPromoCode(promo.code)

      setStatusUser({ status: 'initial' })
      tracking.trackEvent({
        eventName: 'coupon_applied',
        parameters: {},
      })
    } catch (err) {
      const { message } = err as Error

      setStatusUser({ status: 'error' })
    }
  }

  const toogleDiscount = () => {
    if (openDetail) {
      setDetailOpen(true)
      setAutofocusSelector(true)
    } else {
      setIsSubmit(!isSubmit)
    }
  }

  const closeDiscount = () => {
    setIsSubmit(false)
  }

  const ref = useOutsideClick(closeDiscount)

  return (
    <div className="flex flex-row items-center space-x-2" ref={ref}>
      {promoCode && (
        <>
          <Discount />
          <p
            onClick={toogleDiscount}
            className="text-checkout-m md:text-checkout-s cursor-pointer font-normal text-[#5767DD]">
            {promoCode}
          </p>
        </>
      )}

      {!isSubmit && !promotion?.code && (
        <>
          <Discount />
          <p
            onClick={toogleDiscount}
            className="text-checkout-m md:text-checkout-s cursor-pointer font-normal text-[#5767DD] hover:underline">
            ¿Tienes un código de descuento?
          </p>
        </>
      )}

      {isSubmit && !promotion?.code && (
        <>
          <form className="flex w-full" onSubmit={handleSubmit(onSubmit)}>
            <fieldset className="flex w-full flex-row gap-2">
              <div className="w-full">
                <TextField
                  name={'promotionCode'}
                  type={'text'}
                  placeholder={'Añadir código de promoción'}
                  variant={'secondary'}
                  {...register('promotionCode')}
                  className="text-checkout-m border-2 border-[#CCCCCC] bg-white font-normal"
                  labelClassName={'hidden'}
                  error={errors.promotionCode?.message}
                  autoFocus={true}
                />
              </div>

              <ButtonCustom
                loading={statusDiscountForm?.status === 'processing'}
                disabled={!isDirty || !isValid}
                type="submit"
                className="h-min min-h-[42px] bg-[#5767DD] text-sm hover:bg-[#5767DD] disabled:opacity-50">
                Aplicar
              </ButtonCustom>
            </fieldset>
          </form>
        </>
      )}
    </div>
  )
}
